import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import Client from 'shopify-buy';

import { getStorageData, setStorageData } from '~/utils/storage';

export type LineItem = {
  id: number;
  title: string;
  variant: {
    id: string;
    inventoryQuantity: number;
    title: string;
    priceV2: {
      amount: string;
      currencyCode: string;
    };
    compareAtPriceV2: {
      amount: string;
      currencyCode: string;
    };
    image: {
      src: string;
    };
  };
  quantity: number;
  customAttributes: {
    key: string;
    value: string;
  }[];
};

export type DiscountCode = {
  code: string;
  value: {
    amount: string;
    currencyCode: string;
    percentage?: number;
  };
  applicable: boolean;
};

export type CheckoutTypes = {
  id?: string;
  webUrl?: string;
  lineItems: LineItem[];
  discountApplications: DiscountCode[];
  totalPriceV2: {
    currencyCode: string;
    amount: string;
  };
  updatedAt: Date;
};

type CustomAttributesType = {
  key: string;
  value: string;
};

const client = Client.buildClient({
  domain: process.env.GATSBY_SHOPIFY_STORE_URL,
  storefrontAccessToken: process.env.GATSBY_STOREFRONT_ACCESS_TOKEN,
});

const defaultValues = {
  cart: [],
  isOpen: false,
  loadingProductID: '',
  hasAddToCartError: false,
  hasDiscountCodeError: false,
  setHasAddToCartError: (_hasAddToCartError) => {},
  onOpen: () => {},
  onClose: () => {},
  addVariantToCart: (
    _variantId,
    _quantity,
    _customAttributes?: CustomAttributesType[]
  ) => {},
  addVariantToCartByBuyNow: (
    _variantId,
    _quantity,
    _customAttributes?: CustomAttributesType[]
  ) => {},
  removeLineItem: (_checkoutID, _lineItemID) => {},
  applyDiscount: (_checkoutID, _code) => {},
  removeDiscount: (_checkoutID) => {},
  updateLineItem: (_checkoutID, _lineItemID, _quantity) => {},
  client,
  checkout: {
    lineItems: [],
    subtotalPriceV2: {
      amount: '0',
      currencyCode: 'USD',
    },
    totalPriceV2: {
      amount: '0',
      currencyCode: 'USD',
    },
    lineItemsSubtotalPrice: {
      amount: '0',
      currencyCode: 'USD',
    },
    updatedAt: new Date(),
    discountApplications: [],
  } as CheckoutTypes,
  buyNowCheckout: {
    lineItems: [],
    discountApplications: [],
    subtotalPriceV2: {
      amount: '0',
      currencyCode: 'USD',
    },
    totalPriceV2: {
      amount: '0',
      currencyCode: 'USD',
    },
    lineItemsSubtotalPrice: {
      amount: '0',
      currencyCode: 'USD',
    },
    updatedAt: new Date(),
  } as CheckoutTypes,
  didJustAddToCart: false,
  isCartFetching: false,
};
export const StoreContext = createContext(defaultValues);

const localStorageKey = 'shopify_checkout_id';
const localStorageKeyBuyNow = 'shopify_buy_now_checkout_id';
const discountCodeFieldName = 'discountCode';
export const localStorageCartKey = 'cart';

// eslint-disable-next-line max-statements
export const StoreProvider = ({ children }) => {
  const localStorageCheckout =
    (getStorageData(localStorageCartKey) as CheckoutTypes) ||
    defaultValues.checkout;

  const [checkout, setCheckout] = useState<CheckoutTypes>(localStorageCheckout);
  const [buyNowCheckout, setBuyNowCheckout] = useState<CheckoutTypes>(
    defaultValues.checkout
  );
  const [loadingProductID, setLoadingProductID] = useState('');
  const [hasAddToCartError, setHasAddToCartError] = useState(false);
  const [hasDiscountCodeError, setDiscountCodeError] = useState(false);
  const [didJustAddToCart, setDidJustAddToCart] = useState(false);
  const [isCartFetching, setIsCartFetching] = useState(true);
  const setCheckoutItem = (newCheckout) => {
    setStorageData(localStorageKey, newCheckout.id);
    setCheckout(newCheckout);
  };

  useEffect(() => {
    setStorageData(localStorageCartKey, checkout);
    if (checkout.id) setStorageData(localStorageKey, checkout.id);
  }, [checkout, hasDiscountCodeError]);

  useEffect(() => {
    const initializeCheckout = async () => {
      const existingCheckoutID = getStorageData(localStorageKey);
      if (existingCheckoutID) {
        try {
          setIsCartFetching(true);
          const existingCheckout = await client.checkout.fetch(
            existingCheckoutID
          );
          setIsCartFetching(false);
          if (!existingCheckout.completedAt) {
            setCheckoutItem(existingCheckout);

            return;
          }
        } catch (e) {
          setStorageData(localStorageKey, null);
        }
      } else {
        const newCheckout = await client.checkout.create();
        setCheckoutItem(newCheckout);
        setIsCartFetching(false);
      }
    };

    const initializeBuyNowCheckout = async () => {
      const newBuyNowCheckout = await client.checkout.create();
      setStorageData(localStorageKeyBuyNow, newBuyNowCheckout.id);
      setBuyNowCheckout(newBuyNowCheckout);
    };

    initializeBuyNowCheckout();
    initializeCheckout();
  }, []);

  const addVariantToCart = useCallback(
    (variantId, quantity, customAttributes) => {
      setLoadingProductID(variantId);
      const checkoutID = checkout.id;

      const lineItemsToUpdate = [
        {
          variantId,
          quantity: parseInt(quantity, 10),
          customAttributes,
        },
      ];

      return client.checkout
        .addLineItems(checkoutID, lineItemsToUpdate)
        .then((res) => {
          setCheckout(res);
          setDidJustAddToCart(true);
          setTimeout(() => setDidJustAddToCart(false), 3000);
        })
        .catch((error) => {
          setHasAddToCartError(true);
          console.error(
            `error while adding a product: ${variantId} - ${error}`
          );
        })
        .finally(() => setLoadingProductID(''));
    },
    [checkout.id]
  );

  const addVariantToCartByBuyNow = useCallback(
    (variantId, quantity, customAttributes) => {
      const checkoutID = buyNowCheckout.id;
      const lineItemsToUpdate = [
        {
          variantId,
          quantity: parseInt(quantity, 10),
          customAttributes,
        },
      ];
      const existingLineItemIDs = buyNowCheckout.lineItems.map(({ id }) => id);

      return client.checkout
        .removeLineItems(checkoutID, existingLineItemIDs)
        .then(() => {
          client.checkout
            .addLineItems(checkoutID, lineItemsToUpdate)
            .then((res) => {
              setBuyNowCheckout(res);
              window.location.replace(buyNowCheckout.webUrl!);
            })
            .catch((error) => {
              setHasAddToCartError(true);
              console.error(
                `Error while adding a product: ${variantId} - ${error}`
              );
            });
        })
        .catch((error) => {
          setHasAddToCartError(true);
          console.error(`Error while removing products: ${error}`);
        });
    },
    [buyNowCheckout.id, buyNowCheckout.lineItems, buyNowCheckout.webUrl]
  );

  const removeLineItem = (checkoutID, lineItemID) => {
    setLoadingProductID(lineItemID);

    return client.checkout
      .removeLineItems(checkoutID, [lineItemID])
      .then((res) => {
        setCheckout(res);
        setLoadingProductID('');
      });
  };

  const updateLineItem = (checkoutID, lineItemID, quantity) => {
    setLoadingProductID(lineItemID);

    const lineItemsToUpdate = [
      { id: lineItemID, quantity: parseInt(quantity, 10) },
    ];

    return client.checkout
      .updateLineItems(checkoutID, lineItemsToUpdate)
      .then((res) => {
        setCheckout(res);
        setLoadingProductID('');
      });
  };

  const applyDiscount = (checkoutID, discountCode) => {
    client.checkout.addDiscount(checkoutID, discountCode).then((res) => {
      if (res.userErrors.length <= 0) {
        if (discountCode.length <= 0) {
          setDiscountCodeError(true);
        } else {
          setDiscountCodeError(false);
        }

        setCheckout(res);

        return;
      }

      const isDiscountCodeError = res.userErrors.find(
        (errors) =>
          errors.field.filter((field) => field.value === discountCodeFieldName)
            .length > 0
      );

      if (
        isDiscountCodeError ||
        (checkout.discountApplications.length > 0 &&
          !checkout.discountApplications[0].applicable)
      ) {
        setCheckout(res);
        setDiscountCodeError(true);
      }
    });
  };

  const removeDiscount = (checkoutID) =>
    client.checkout.removeDiscount(checkoutID).then((res) => {
      setCheckout(res);
    });

  const providerValue = useMemo(
    () => ({
      ...defaultValues,
      addVariantToCart,
      addVariantToCartByBuyNow,
      removeLineItem,
      updateLineItem,
      checkout,
      buyNowCheckout,
      loadingProductID,
      didJustAddToCart,
      hasAddToCartError,
      hasDiscountCodeError,
      setHasAddToCartError,
      isCartFetching,
      applyDiscount,
      removeDiscount,
    }),
    [
      addVariantToCart,
      addVariantToCartByBuyNow,
      checkout,
      buyNowCheckout,
      didJustAddToCart,
      hasAddToCartError,
      isCartFetching,
      loadingProductID,
    ]
  );

  return (
    <StoreContext.Provider value={providerValue}>
      {children}
    </StoreContext.Provider>
  );
};
